import React from 'react';
import { Check, MessageCircle, Users, Zap, Shield, BarChart3, Clock, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import Navbar from './Navbar';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';

const WhatsAppPage = () => {
  const { login } = useAuth();

  const whyChoose = [
    { icon: Shield, title: 'Tecnologia Aprovada pela Meta', desc: 'Parceiro oficial com API verificada' },
    { icon: Zap, title: 'WhatsApp Business API', desc: 'Última versão oficial integrada' },
    { icon: Star, title: 'Suporte Mais Rápido', desc: 'Baseado em avaliações de clientes' },
  ];

  const useCases = [
    { title: 'Recuperação de Carrinho', desc: 'Recupere vendas perdidas automaticamente' },
    { title: 'Descontos Personalizados', desc: 'Ofertas exclusivas para cada cliente' },
    { title: 'Alertas de Estoque', desc: 'Notifique quando produtos voltarem' },
    { title: 'Venda Cruzada', desc: 'Recomende produtos complementares' },
    { title: 'Geração de Leads', desc: 'Capture contatos qualificados' },
    { title: 'Recomendações', desc: 'Sugira produtos baseado no perfil' },
  ];

  const features = [
    { title: '98% de Taxa de Abertura', desc: 'Mensagens lidas na plataforma preferida dos clientes' },
    { title: 'Comunicação Personalizada', desc: 'Segmente e analise sua audiência para ofertas relevantes' },
    { title: 'Conversão Automática', desc: 'Bot automatiza jornada do cliente e aumenta vendas' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-28 pb-16">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Faça do WhatsApp seu{' '}
                  <span className="text-green-500">principal canal</span>{' '}
                  de marketing
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Use seus chats do WhatsApp para construir relacionamentos sólidos com clientes, 
                  aumentando a lealdade e as vendas de forma espetacular.
                </p>
                <Button 
                  onClick={login}
                  size="lg" 
                  className="bg-green-500 hover:bg-green-600 text-lg px-8 py-6 h-auto"
                  data-testid="whatsapp-cta-button"
                >
                  Iniciar teste gratuito de 7 dias
                </Button>
              </div>
              <div className="relative">
                <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm mx-auto">
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <MessageCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">ChatAlfa Bot</p>
                      <p className="text-sm text-gray-500">Online agora</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-gray-100 rounded-2xl rounded-tl-none p-3 max-w-[80%]">
                      <p className="text-sm">Olá! Como posso ajudar você hoje? 👋</p>
                    </div>
                    <div className="bg-green-500 text-white rounded-2xl rounded-tr-none p-3 max-w-[80%] ml-auto">
                      <p className="text-sm">Quero saber sobre os produtos</p>
                    </div>
                    <div className="bg-gray-100 rounded-2xl rounded-tl-none p-3 max-w-[80%]">
                      <p className="text-sm">Claro! Temos ofertas especiais para você. Veja nosso catálogo:</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-12">Por que escolher ChatAlfa?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {whyChoose.map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3b82f6] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                A maioria dos seus clientes já usa WhatsApp
              </h2>
              <p className="text-xl text-gray-600">
                Aproveite esta oportunidade - converta contatos em clientes pagantes automaticamente!
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature) => (
                <Card key={feature.title} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-8 pb-6">
                    <h3 className="text-xl font-bold text-[#3b82f6] mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="py-16 bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-white mb-4">
              Casos de uso mais populares
            </h2>
            <p className="text-center text-blue-100 mb-12">
              Automatize processos e aumente suas vendas com WhatsApp
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {useCases.map((useCase) => (
                <Card key={useCase.title} className="bg-white/10 backdrop-blur border-white/20 text-white">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg mb-2">{useCase.title}</h3>
                    <p className="text-blue-100 text-sm">{useCase.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* ChatGPT Integration */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="h-4 w-4" />
              Integração com IA
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Seja o primeiro a usar ChatGPT diretamente nos seus chats do WhatsApp
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Com uma prueba gratuita de 7 dias
            </p>
            <Button 
              onClick={login}
              size="lg" 
              className="bg-[#3b82f6] hover:bg-[#2563eb] text-lg px-8"
              data-testid="whatsapp-chatgpt-cta"
            >
              Iniciar teste gratuito
            </Button>
          </div>
        </div>

        {/* Meta Integration */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Shield className="h-4 w-4" />
                  Parceiro Oficial Meta
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Vá além com uma integração 100% segura
                </h2>
                <p className="text-gray-600 mb-6">
                  ChatAlfa é um provedor de tecnologia aprovado pela Meta. 
                  A última versão da WhatsApp Business API está integrada diretamente.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>API oficial verificada</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Conformidade com LGPD/GDPR</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Dados criptografados</span>
                  </li>
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
                      <span className="text-white text-2xl font-bold">M</span>
                    </div>
                    <div>
                      <p className="font-bold text-xl">Meta</p>
                      <p className="text-gray-500">Business Partner</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Preview */}
        <div className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Preços</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-2 border-[#3b82f6]">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Negócio</h3>
                  <p className="text-gray-600 mb-4">Para empresas começando com chatbots</p>
                  <div className="mb-6">
                    <span className="text-sm text-gray-500">a partir de</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">R$119</span>
                      <span className="text-gray-500">/mês</span>
                    </div>
                  </div>
                  <Button onClick={login} className="w-full bg-[#3b82f6] hover:bg-[#2563eb]">
                    Iniciar teste gratuito
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Empresa</h3>
                  <p className="text-gray-600 mb-4">Plano personalizado com suporte dedicado</p>
                  <div className="mb-6">
                    <span className="text-sm text-gray-500">a partir de</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">R$2.000</span>
                      <span className="text-gray-500">/mês</span>
                    </div>
                  </div>
                  <Button onClick={login} variant="outline" className="w-full">
                    Agendar consulta
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Perguntas Frequentes</h2>
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">O que é um chatbot de WhatsApp?</h3>
                  <p className="text-gray-600 text-sm">
                    Um chatbot de WhatsApp é um software que utiliza regras pré-programadas ou 
                    inteligência artificial (IA) para automatizar conversas no WhatsApp: envia 
                    mensagens em tempo real, 24 horas por dia, 7 dias por semana.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Como funciona um chatbot de WhatsApp?</h3>
                  <p className="text-gray-600 text-sm">
                    Os bots de WhatsApp automatizam o atendimento ao cliente, melhoram a experiência 
                    respondendo mensagens, coletando dados e muito mais.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Quanto custa um chatbot de WhatsApp?</h3>
                  <p className="text-gray-600 text-sm">
                    Na maioria das plataformas, criar um chatbot é gratuito. Os custos dependem do 
                    plano escolhido e se você usa a API do WhatsApp Business.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* CTA Final */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Automatize sua comunicação no WhatsApp
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              com uma prueba gratuita de 7 dias
            </p>
            <Button 
              onClick={login}
              size="lg" 
              className="bg-green-500 hover:bg-green-600 text-lg px-8 py-6 h-auto"
              data-testid="whatsapp-final-cta"
            >
              Iniciar teste gratuito
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WhatsAppPage;
